import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import UserMenu from './UserMenu.js';
import { getAdminBadge } from '../utils/getAdminBadge.js';

const CockpitNavBar = ({
  cockpit,
  isAdmin,
  localActiveTab,
  handleTabChange,
  currentUser,
  navigateToAdminEdit,
  darkMode,
  toggleDarkMode,
  logout
}) => {
  const [isTabsMenuOpen, setIsTabsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const hoverTimeoutRef = useRef(null);


  const handleMouseEnter = useCallback((key) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setOpenDropdown(key);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  }, []);

  // Create navbar items structure
  const navbarItems = useMemo(() => {
    if (!cockpit?.config?.tabs) return {};

    const items = {};

    cockpit.config.tabs.forEach((tab, originalIndex) => {
      // Skip tabs that user cannot access
      if (tab.admin_only && !isAdmin) {
        return;
      }

      if (tab.group === null || tab.group === undefined) {
        // Individual tab (no group) - store tab.name directly
        items[tab.name] = {
          type: 'tab',
          name: tab.name,
          originalIndex: originalIndex,
          ...tab
        };
      } else {
        // Group tabs - if group doesn't exist, create array
        if (!items[tab.group]) {
          items[tab.group] = [];
        }
        // Add tab to the group array
        items[tab.group].push({
          name: tab.name,
          originalIndex: originalIndex,
          ...tab
        });
      }
    });

    return items;
  }, [cockpit, isAdmin]);


  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md relative z-20">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        {/* Mobile: Hamburger menu for tabs */}
        <div className="md:hidden flex items-center relative">
          <button
            onClick={() => setIsTabsMenuOpen(!isTabsMenuOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Mobile: Current tab name */}
          <div className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
            {cockpit.config.tabs[localActiveTab]?.name || 'Onglet'}
          </div>

          {/* Mobile: Tabs dropdown */}
          {isTabsMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsTabsMenuOpen(false)}
              />
              <div className="absolute left-0 top-10 mt-2 w-64 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-40">
                <div className="py-1">
                  {Object.entries(navbarItems).map(([key, value], index) => {
                    if (!Array.isArray(value)) {
                      // Objet JSON (pas array) - Tab individuel cliquable
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            handleTabChange(value.originalIndex);
                            setIsTabsMenuOpen(false);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            index > 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''
                          } ${
                            localActiveTab === value.originalIndex
                              ? 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center">
                            {value.name}
                            {value.admin_only && (
                              getAdminBadge()
                            )}
                          </div>
                        </button>
                      );
                    } else {
                      return (
                        <div key={key}>
                          <div className={`px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                            index > 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''
                          }`}>
                            {key}
                          </div>
                          {value.map((tab) => (
                            <button
                              key={tab.originalIndex}
                              onClick={() => {
                                handleTabChange(tab.originalIndex);
                                setIsTabsMenuOpen(false);
                              }}
                              className={`block w-full text-left px-6 py-2 text-sm ${
                                localActiveTab === tab.originalIndex
                                  ? 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400'
                                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center">
                                {tab.name}
                                {tab.admin_only && (
                                  getAdminBadge()
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Desktop: Perfectly Centered Navbar */}
        <div className="hidden md:flex flex-1 justify-center items-center">
          <nav className="flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full px-6 py-2 shadow-lg border border-gray-200/50 dark:border-gray-700/50">
            {Object.entries(navbarItems).map(([key, value]) => {
              if (!Array.isArray(value)) {
                return (
                  <button
                    key={key}
                    onClick={() => handleTabChange(value.originalIndex)}
                    className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      localActiveTab === value.originalIndex
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="font-medium">{value.name}</span>
                      {value.admin_only && (
                        getAdminBadge()
                      )}
                    </div>
                  </button>
                );
              } else {

                const isActive = value.some(tab => localActiveTab === tab.originalIndex);
                const isDropdownOpen = openDropdown === key;

                return (
                  <div
                    key={key}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(key)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => setOpenDropdown(isDropdownOpen ? null : key)}
                      className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-md'
                          : isDropdownOpen
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                      }`}
                    >
                      <span className="font-medium">{key}</span>
                      <svg
                        className={`ml-2 h-4 w-4 transition-transform duration-200 ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown menu - Absolutely positioned above everything */}
                    {isDropdownOpen && (
                      <div
                        className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-72 rounded-xl shadow-2xl bg-white dark:bg-gray-800 ring-1 ring-black/5 dark:ring-white/10 z-[10000] overflow-hidden"
                        style={{
                          position: 'absolute',
                          zIndex: 10000
                        }}
                      >
                        <div className="py-2">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                            {key}
                          </div>
                          {value.map((tab) => (
                            <button
                              key={tab.originalIndex}
                              onClick={() => {
                                handleTabChange(tab.originalIndex);
                                setOpenDropdown(null);
                              }}
                              className={`block w-full text-left px-4 py-3 text-sm transition-all duration-150 ${
                                localActiveTab === tab.originalIndex
                                  ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border-r-2 border-blue-600'
                                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{tab.name}</span>
                                <div className="flex items-center space-x-2">
                                  {tab.admin_only && (
                                    getAdminBadge()
                                  )}
                                  {localActiveTab === tab.originalIndex && (
                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </nav>
        </div>

        <div className="flex items-center">
          {/* Public indicator */}
          {cockpit.is_public && (
            <div className="mr-3 flex items-center text-sm text-green-600 dark:text-green-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span>Public</span>
            </div>
          )}

          {/* Edit icon for superadmins */}
          {currentUser && (currentUser.isadmin || currentUser.issuperadmin) && (
            <button
              onClick={navigateToAdminEdit}
              className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 focus:outline-none mr-2 flex items-center gap-2"
              title="Modifier ce cockpit"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Editer
            </button>
          )}

          {/* User menu */}
          <UserMenu
            currentUser={currentUser}
            logout={logout}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
          />
        </div>
      </div>
    </div>
  );
};

export default CockpitNavBar;